import * as React from "react";

import * as utils from "@client/utils";

import {calculateRoundedDayMetrics, getMetrics} from "@client/calculations";
import {Macros, PieChart} from "@client/components/PieChart";
import {dayFormStyles as styles} from "@client/components/styles";
import {Form} from "@client/forms";
import type {Props as JournalProps} from "@client/scenes/Journal";
import * as forms from "@client/site/forms";
import {server} from "@reactivated";

interface Props {
    useKilojoules: boolean;
    day: server.journal.nutrition.Day.output;
    target: server.journal.nutrition.Target.output | null;
    targets: server.journal.nutrition.Target.output[];
    pForm: JournalProps["pForm"];
    profile: server.core.rpc.Profile.output;
}

export const DayForm = (props: Props) => {
    const {total, remaining, target} = calculateRoundedDayMetrics(
        props.day,
        props.target,
        props.useKilojoules,
    );
    const {pForm} = props;

    return (
        <Form>
            {props.day.comments.map((comment, index) => (
                <div key={index}>
                    <forms.Label
                        label={`${comment.manager.profile.name} commented ${utils.formatDistanceToNow(
                            comment.created_at,
                        )} ago`}
                        error={null}
                    >
                        <div className="whitespace-pre-wrap">{comment.comment}</div>
                    </forms.Label>
                </div>
            ))}
            {props.profile.has_hidden_macros == false && (
                <>
                    <div className={styles.row}>
                        <div className={styles.firstHeader} />
                        <div className={styles.header}>Total</div>
                        <div className={styles.header}>Target</div>
                        <div className={styles.header}>Remaining</div>
                    </div>
                    {getMetrics({useKilojoules: props.useKilojoules}).CORE_METRICS.map(
                        ([metric, , , unit, label]) => (
                            <div key={metric} className={styles.row}>
                                <div className={styles.label}>{label}</div>
                                <div className={styles.total}>
                                    {`${total[metric] ?? "0"} ${unit}`}
                                </div>
                                <div className={styles.target}>
                                    {target[metric] ?? "-"}
                                </div>
                                <div className={styles.remaining}>
                                    {remaining[metric] ?? "-"}
                                </div>
                            </div>
                        ),
                    )}
                    <div className={styles.optionalSection}>
                        {getMetrics({
                            useKilojoules: props.useKilojoules,
                        }).OPTIONAL_METRICS.map(([metric, , , unit, label]) => (
                            <div key={metric} className={styles.optionalRow}>
                                <div className={styles.optionalLabel}>{label}</div>
                                <div className={styles.optionalMacro}>
                                    {total[metric] != null
                                        ? `${total[metric]} ${unit}`
                                        : "-"}
                                </div>
                                <div className={styles.optionalMacro}>
                                    {target[metric] ?? "-"}
                                </div>
                                <div className={styles.optionalMacro}>
                                    {remaining[metric] ?? "-"}
                                </div>
                            </div>
                        ))}
                    </div>
                    <Macros>
                        <PieChart {...total.summary} />
                    </Macros>
                </>
            )}
            {props.targets.length > 1 && (
                <forms.Label label="Change macro targets" error={null}>
                    <forms.PWidget field={pForm.fields.target} />
                </forms.Label>
            )}
            <forms.Label label="Notes" error={null}>
                <forms.PWidget field={pForm.fields.notes} />
            </forms.Label>
            <div className="flex items-center gap-2">
                <forms.PWidget field={pForm.fields.include_in_reports} />
                <forms.Label label="Include in reports" error={null} />
            </div>
        </Form>
    );
};
